import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    exhaustMap,
    forkJoin,
    map,
    of,
    switchMap,
    withLatestFrom,
} from "rxjs";
import { InvitesApiService } from "../data-access/invites-api.service";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";
import { InvitesActions } from "./invites.actions";
import { invitesFeature } from "./invites.reducer";

@Injectable()
export class InvitesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(InvitesApiService);

    readonly open$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InvitesActions.pageOpened),
            withLatestFrom(
                this.store.select(invitesFeature.selectInvitesState)
            ),
            switchMap(([, state]) =>
                forkJoin({
                    page: this.api.loadPage(""),
                    total: this.api.loadCount(),
                }).pipe(
                    withSkeletonTiming(),
                    map(({ page, total }) =>
                        InvitesActions.pageLoaded({
                            ...page,
                            total,
                            token: "",
                            pageIndex: 0,
                        })
                    ),
                    catchError(() => of(InvitesActions.pageLoadFailed()))
                )
            )
        )
    );

    readonly page$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InvitesActions.pageRequested),
            withLatestFrom(
                this.store.select(invitesFeature.selectInvitesState)
            ),
            switchMap(([{ token, pageIndex }, state]) =>
                this.api.loadPage(token).pipe(
                    map((page) =>
                        InvitesActions.pageLoaded({
                            ...page,
                            total: state.total,
                            token,
                            pageIndex,
                        })
                    ),
                    catchError(() => of(InvitesActions.pageLoadFailed()))
                )
            )
        )
    );

    readonly resend$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InvitesActions.resendRequested),
            exhaustMap(({ invite }) =>
                this.api.resend(invite).pipe(
                    map(() => InvitesActions.resendSucceeded({ invite })),
                    catchError(() =>
                        of(InvitesActions.resendFailed({ invite }))
                    )
                )
            )
        )
    );

    readonly revoke$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InvitesActions.revokeRequested),
            exhaustMap(({ invite }) =>
                this.api.revoke(invite.gmvid).pipe(
                    map(() => InvitesActions.revokeSucceeded({ invite })),
                    catchError(() =>
                        of(InvitesActions.revokeFailed({ invite }))
                    )
                )
            )
        )
    );

    readonly refreshAfterRevoke$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InvitesActions.revokeSucceeded),
            withLatestFrom(
                this.store.select(invitesFeature.selectInvitesState)
            ),
            map(([, state]) =>
                InvitesActions.pageRequested({
                    token: state.pageTokens[state.pageIndex] ?? "",
                    pageIndex: state.pageIndex,
                })
            )
        )
    );
}
